/**
 * Transactions Hook
 * Provides transaction management operations with React Query
 *
 * @description
 * This hook provides:
 * - Create transaction mutation
 * - Fetch transactions with pagination and filters
 * - Fetch single transaction by ID
 * - Automatic cache invalidation
 *
 * @author SAUBHAGYA Development Team
 * @since Story 2-5
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  manufacturingApi,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  QCVerificationRequest,
  TransactionDTO,
  TransactionSummaryResponse,
  PageResponse,
} from '@/services/manufacturing/manufacturingApi';

/**
 * Hook to create a new transaction
 * @returns Mutation object with createTransaction function
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTransactionRequest) =>
      manufacturingApi.createTransaction(data),
    onSuccess: () => {
      // Invalidate and refetch transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-summary'] });
    },
  });
}

/**
 * Hook to fetch transactions with optional filters
 * @param filters - Optional filters (status, gaushalaSearch, grade, materialTypeId, from, to, page, size)
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with paginated transactions
 */
export function useTransactions(
  filters?: {
    status?: string | string[];
    gaushalaSearch?: string;
    gaushalaIds?: number[];
    grade?: string;
    materialTypeId?: string | number;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  },
  enabled: boolean = true
) {
  // Debug logging for status filter
  console.log('[useTransactions] Filters received:', JSON.stringify(filters, null, 2));
  console.log('[useTransactions] Status type:', typeof filters?.status, 'isArray:', Array.isArray(filters?.status));

  return useQuery<PageResponse<TransactionDTO>>({
    queryKey: ['transactions', filters],
    queryFn: () => manufacturingApi.getTransactions(filters),
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    retry: 2,
  });
}

/**
 * Hook to fetch a single transaction by ID
 * @param id - Transaction ID
 * @param enabled - Whether to enable the query (default: true)
 * @returns Query result with transaction details
 */
export function useTransaction(id: number, enabled: boolean = true) {
  return useQuery<TransactionDTO>({
    queryKey: ['transaction', id],
    queryFn: () => manufacturingApi.getTransactionById(id),
    enabled: enabled && id > 0,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
    retry: 2,
  });
}

/**
 * Hook to manually refetch transactions
 * @returns Function to refetch transactions
 */
export function useRefetchTransactions() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
  };
}

/**
 * Hook to submit QC verification for a transaction
 * @returns Mutation object with submitQC function
 */
export function useSubmitQC() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      data,
    }: {
      transactionId: number;
      data: QCVerificationRequest;
    }) => manufacturingApi.submitQCVerification(transactionId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-summary'] });
      // Invalidate specific transaction
      queryClient.invalidateQueries({
        queryKey: ['transaction', variables.transactionId],
      });
    },
  });
}

/**
 * Hook to update an UP_COMING transaction
 * @returns Mutation object with updateTransaction function
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      data,
    }: {
      transactionId: number;
      data: UpdateTransactionRequest;
    }) => manufacturingApi.updateTransaction(transactionId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-summary'] });
      // Invalidate specific transaction
      queryClient.invalidateQueries({
        queryKey: ['transaction', variables.transactionId],
      });
    },
  });
}

/**
 * Hook to fetch transaction summary metrics for stat cards.
 * Returns all-time counts by status and total quantity from backend.
 * @returns Query result with transaction summary metrics
 */
export function useTransactionSummary() {
  return useQuery<TransactionSummaryResponse>({
    queryKey: ['transaction-summary'],
    queryFn: () => manufacturingApi.getTransactionSummary(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Hook to approve a driver collection (UP_COMING → INITIALIZED)
 * @returns Mutation object with approveDriverCollection function
 */
export function useApproveDriverCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: number) =>
      manufacturingApi.approveDriverCollection(transactionId),
    onSuccess: (_, transactionId) => {
      // Invalidate and refetch transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-summary'] });
      // Invalidate specific transaction
      queryClient.invalidateQueries({
        queryKey: ['transaction', transactionId],
      });
    },
  });
}
